/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { useState, useEffect } from 'react';
// eslint-disable-next-line camelcase
import { unstable_batchedUpdates } from 'react-dom';
// % protected region % [Add any extra imports here] off begin
// % protected region % [Add any extra imports here] end

// % protected region % [Customise useAsyncLoading here] off begin
type useAsyncLoading = {
	type: 'loading',
	error: undefined,
	data: undefined,
	refresh: () => void,
}
// % protected region % [Customise useAsyncLoading here] end

// % protected region % [Customise useAsyncError here] off begin
type useAsyncError<E> = {
	type: 'error',
	error: E,
	data: undefined,
	refresh: () => void,
}
// % protected region % [Customise useAsyncError here] end

// % protected region % [Customise useAsyncData here] off begin
type useAsyncData<T> = {
	type: 'data',
	error: undefined,
	data: T,
	refresh: () => void,
}
// % protected region % [Customise useAsyncData here] end

// % protected region % [Customise useAsyncResponse here] off begin
export type useAsyncResponse<T, E> = useAsyncLoading | useAsyncError<E> | useAsyncData<T>;
// % protected region % [Customise useAsyncResponse here] end

// % protected region % [Customise useAsyncOptions here] off begin
export interface useAsyncOptions {
	/** Should the hook transition to the loading state after the first load. */
	suppressSubsequentLoadingState?: boolean
}
// % protected region % [Customise useAsyncOptions here] end

// % protected region % [Customise useAsyncFn here] off begin
export type useAsyncFn<T> = (() => Promise<T>) | ((abortSignal: AbortSignal) => Promise<T>);
// % protected region % [Customise useAsyncFn here] end

// % protected region % [Customise useAsync here] off begin
export default function useAsync<T = any, E = any>(
	fn: useAsyncFn<T>,
	deps?: readonly any[],
	options?: useAsyncOptions,
): useAsyncResponse<T, E> {
	const {
		suppressSubsequentLoadingState = false,
	} = options ?? {};

	const [data, setData] = useState<T>();
	const [error, setError] = useState<E>();
	const [type, setType] = useState<'loading' | 'error' | 'data'>('loading');
	const [refresh, setRefresh] = useState(false);

	useEffect(() => {
		const abortController = new AbortController();

		if (!suppressSubsequentLoadingState && !abortController.signal.aborted) {
			setType('loading');
		}

		fn(abortController.signal)
			.then(x => {
				if (abortController.signal.aborted) {
					return;
				}

				unstable_batchedUpdates(() => {
					setError(undefined);
					setData(x);
					setType('data');
				});
			})
			.catch(x => {
				if (abortController.signal.aborted) {
					return;
				}

				unstable_batchedUpdates(() => {
					setError(x);
					setData(undefined);
					setType('error');
				});
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps === undefined ? undefined : [refresh, ...deps]);

	// Cast to any since we know better than the compiler in this case. Since the function has an explicit return type
	// the function will be externally consistent.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return {
		error,
		data,
		type,
		refresh: () => setRefresh(current => !current),
	} as any;
}
// % protected region % [Customise useAsync here] end
